import { useAuthenticationStore } from "@/store/authentication.store";
import { UUID_SEGMENT_REGEX } from "@/constants/regex";

type OrgRef = {
  uuid?: string;
  type_name?: string;
  type?: string;
} | null;

type AuthSnapshot = {
  selectedCustomer?: OrgRef;
  selectedMsp?: OrgRef;
  selectedAccount?: OrgRef;
};

const cleanSuffix = (suffix: string) => {
  if (!suffix) return "";
  return suffix.startsWith("/") ? suffix : `/${suffix}`;
};

let mockAuth: AuthSnapshot | null = null;

const getAuth = (): AuthSnapshot => {
  if (mockAuth) return mockAuth;
  try {
    const s = useAuthenticationStore();
    return {
      selectedCustomer: s.selectedCustomer,
      selectedMsp: s.selectedMsp,
      selectedAccount: s.selectedAccount,
    };
  } catch {
    // In circular import scenarios (e.g., nav seeds during store init) or when
    // no active pinia exists yet, fall back to empty context.
    return {};
  }
};

export const currentOrgUuid = (orgType?: string) => {
  const { selectedCustomer, selectedMsp, selectedAccount } = getAuth();
  const routeOrgUuid =
    (() => {
      if (typeof location === "undefined") return null;
      const parts = location.pathname.split("/").filter(Boolean);
      if (
        parts.length >= 2 &&
        parts[0] === orgType &&
        UUID_SEGMENT_REGEX.test(parts[1])
      ) {
        return parts[1];
      }
      return null;
    })() || null;

  const selectedAccountType =
    selectedAccount?.type_name?.toLowerCase?.() ||
    selectedAccount?.type?.toLowerCase?.() ||
    null;

  if (orgType === "customer") {
    if (selectedCustomer?.uuid) return selectedCustomer.uuid;
    if (selectedAccountType === "customer" || !selectedAccountType)
      return selectedAccount?.uuid || null;
    if (routeOrgUuid) return routeOrgUuid as string;
    return null;
  }

  if (orgType === "msp") {
    if (selectedMsp?.uuid) return selectedMsp.uuid;
    if (selectedAccountType === "msp" || !selectedAccountType)
      return selectedAccount?.uuid || null;
    if (routeOrgUuid) return routeOrgUuid as string;
    return null;
  }

  if (orgType === "system") {
    if (selectedAccountType === "system" || !selectedAccountType)
      return selectedAccount?.uuid || null;
    if (routeOrgUuid) return routeOrgUuid as string;
  }

  return (
    selectedCustomer?.uuid ||
    selectedMsp?.uuid ||
    selectedAccount?.uuid ||
    (routeOrgUuid as string | null) ||
    null
  );
};

/**
 * Build a scoped URL of the form /<orgType>/<orgUuid>/<suffix?>
 * - orgType: "system" | "msp" | "customer"
 * - orgUuid: explicit UUID; if omitted/null, falls back to selectedAccount.uuid
 * - suffix: optional trailing path (with or without leading slash)
 */
export const buildOrgUrl = (
  orgType: string,
  orgUuid?: string | null,
  suffix = "",
): string => {
  const uuid = orgUuid || currentOrgUuid(orgType);
  const trailing = cleanSuffix(suffix);
  if (!uuid) {
    // Return original suffix if we cannot build a scoped URL.
    return `/${orgType}${trailing}`;
  }
  return `/${orgType}/${uuid}${trailing}`;
};

// Test hook to override the auth context in unit tests
export const __setMockStore = (auth: AuthSnapshot | null) => {
  mockAuth = auth;
};

export default buildOrgUrl;
